import { formatGBP } from './money';

// Shared budget formatter for the 2026-06 budget-range migration.
// One source of truth for how a (minimum_budget, maximum_budget) pair
// renders across the app: BriefDetail, AmendBriefPanel, supplier portal,
// emails. Inputs come straight off a brief row.
//
// Buckets:
//   min=null  max=null   →  null               (caller renders "—")
//   min=null  max=N      →  "Up to £N"         (legacy single-ceiling)
//   min=N     max=null   →  "£N+"
//   min=N     max=M      →  "£N – £M"
//
// VAT suffix is appended by callers via the second argument; the
// formatter focuses on the numeric shape so a caller rendering inside a
// chip ("£10k – £20k") can omit the VAT phrase without re-implementing
// the bucket logic.
//
// Strings use the en-GB separator (1,000) and an en-dash for the range
// to match the broader copy standard (see the row-sync brief §5 — em
// dash for prose, en dash for numeric ranges).

// Delegates to the shared formatter, but keeps this module's own contract:
// returns NULL (not an em-dash) when there's no usable figure, because
// callers test for null to decide whether to render the range at all.
// Budgets are always whole pounds here, hence the round + no pence.
const gbp = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return null;
  return formatGBP(Math.round(num));
};

// Where a single quote sits against the brief budget, for the compare
// screen's per-quote context line ("92% under budget" / "18% over budget").
// The reference is the ceiling (maximum_budget) when set, else the floor
// (minimum_budget) — "under budget" means under the most Fusion would pay.
// Returns null when there's no budget to compare against (caller omits the
// line entirely) or the price is missing. `pct` is a whole-number percent,
// `direction` is 'under' | 'over'. Both figures are ex-VAT (net), matching
// total_price and the stored budget columns.
export function budgetPosition(price, brief) {
  if (!brief) return null;
  const maxRaw = brief.maximum_budget;
  const minRaw = brief.minimum_budget;
  const ref = (maxRaw != null && maxRaw !== '') ? Number(maxRaw)
            : (minRaw != null && minRaw !== '') ? Number(minRaw)
            : null;
  const p = Number(price);
  if (ref == null || !Number.isFinite(ref) || ref <= 0 || !Number.isFinite(p)) return null;
  const pct = Math.round(Math.abs(p - ref) / ref * 100);
  return { pct, direction: p > ref ? 'over' : 'under', ref };
}

export function fmtBudgetRange(brief, { withVat = false } = {}) {
  if (!brief) return null;
  const minRaw = brief.minimum_budget;
  const maxRaw = brief.maximum_budget;
  const hasMin = minRaw != null && minRaw !== '';
  const hasMax = maxRaw != null && maxRaw !== '';
  if (!hasMin && !hasMax) return null;
  const minStr = hasMin ? gbp(minRaw) : null;
  const maxStr = hasMax ? gbp(maxRaw) : null;
  // VAT convention is "ex-VAT" everywhere (awarded-brief-state brief §7) —
  // consistent with the quote form + award value; never "+ VAT".
  const vatSuffix = withVat ? ' ex-VAT' : '';
  if (hasMin && hasMax) return `${minStr} – ${maxStr}${vatSuffix}`;
  if (hasMax)           return `Up to ${maxStr}${vatSuffix}`;
  return `${minStr}+${vatSuffix}`;
}
